
import { useState } from 'react'
import { motion } from 'framer-motion'
import { UserPlus } from 'lucide-react'
import { Body, Modal, Button, ButtonVariant, ButtonStyle, Input, INPUT_MASKS } from '@meiuca/ui-components'

interface GuestInviteModalProps {
  open: boolean
  onClose: () => void
  onInviteGuest: (name: string, phoneNumber?: string) => Promise<void>
  isLoading?: boolean
}

export function GuestInviteModal({ open, onClose, onInviteGuest, isLoading }: GuestInviteModalProps) {
  const [name, setName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [errors, setErrors] = useState<{ name?: string; phoneNumber?: string; general?: string }>({})

  const validateForm = () => {
    const newErrors: { name?: string; phoneNumber?: string } = {}
    
    if (!name.trim()) {
      newErrors.name = 'Nome é obrigatório'
    }

    if (phoneNumber) {
      const cleaned = phoneNumber.replace(/\D/g, '')
      // Valida se tem 10 ou 11 dígitos (formato brasileiro sem código do país)
      if (cleaned.length < 10 || cleaned.length > 11) {
        newErrors.phoneNumber = 'Telefone deve ter 10 ou 11 dígitos'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setErrors({})
      const formattedPhone = phoneNumber.trim() ? formatPhoneForAPI(phoneNumber.trim()) : undefined
      await onInviteGuest(name.trim(), formattedPhone)
      handleClose()
    } catch (error: any) {
      setErrors({ general: error.message || 'Erro ao convidar convidado' })
    }
  }

  const handleClose = () => {
    setName('')
    setPhoneNumber('')
    setErrors({})
    onClose()
  }

  const formatPhoneNumber = (value: string) => {
    // A máscara do Input component já vai formatar automaticamente
    // Aqui apenas guardamos o valor recebido
    return value
  }

  const formatPhoneForAPI = (phoneNumber: string): string => {
    if (!phoneNumber || phoneNumber.trim() === '') return ''
    
    // Remove tudo exceto dígitos
    const cleaned = phoneNumber.replace(/\D/g, '')
    
    // Se já começar com 55, retorna com +55
    if (cleaned.startsWith('55') && cleaned.length >= 12) {
      return `+${cleaned}`
    }
    
    // Se não começar com 55, adiciona +55
    return `+55${cleaned}`
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Convidar Convidado"
      headerIcon={UserPlus}
      size="md"
      appearance={{
        backgroundColor: '#ffffff',
        textColor: '#1f2937',
        overlayColor: 'rgba(0, 0, 0, 0.6)'
      }}
    >
      <div className="space-y-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <UserPlus className="w-8 h-8 text-green-600" />
        </div>
        
        <div className="text-center">
          <Body className="text-gray-600">
            Convide uma pessoa que ainda não tem conta no Meiuca para participar do jogo.
          </Body>
        </div>

        <div className="space-y-4">
          <Input
            label="Nome completo"
            placeholder="Digite o nome da pessoa"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            errorMessage={errors.name}
            required
            fullWidth
          />

          <Input
            label="Telefone (opcional)"
            placeholder="(19) 99999-9999"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
            mask={INPUT_MASKS.PHONE_BR}
            error={!!errors.phoneNumber}
            errorMessage={errors.phoneNumber}
            helperText="Ex: (11) 99999-9999 - será enviado como +5511999999999"
            fullWidth
          />

          {errors.general && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-4 bg-red-50 border border-red-200 rounded-lg"
            >
              <Body className="text-red-700">{errors.general}</Body>
            </motion.div>
          )}
        </div>

        <div className="flex space-x-3 pt-4">
          <Button
            buttonStyle={ButtonStyle.BORDERED}
            onClick={handleClose}
            className="flex-1"
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={handleSubmit}
            className="flex-1"
            loading={isLoading}
          >
            <UserPlus className="w-4 h-4" />
            Convidar
          </Button>
        </div>
      </div>
    </Modal>
  )
}